/**
 * SearchableSelect.jsx
 * Componente de Select com busca/filtro integrado
 *
 * Substitui Select + MenuItem para listas grandes (devices, users, pois, etc)
 * Usa Autocomplete do MUI com visual Premium
 *
 * Props:
 *   - options: array de objetos
 *   - value: valor selecionado (id)
 *   - onChange: callback (value) => void
 *   - label: label do campo
 *   - keyGetter: (item) => id (default: item.id)
 *   - titleGetter: (item) => string (default: item.name)
 *   - renderOption: custom render para opcoes
 *   - emptyOption: objeto { value, label } para opcao vazia
 *   - disabled: boolean
 *   - error: boolean
 *   - helperText: string
 *   - required: boolean
 *   - placeholder: string
 *   - startAdornment: ReactNode (icone antes do texto)
 *   - groupBy: (option) => string (para agrupar opcoes)
 *   - filterOptions: custom filter function
 *   - noOptionsText: texto quando nao ha resultados
 *   - loading: boolean
 *   - loadingText: texto durante loading
 */

import React, { useMemo, forwardRef } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { usePremiumTokens } from '../theme/usePremiumTokens';

const SearchableSelect = forwardRef(({
  options = [],
  value,
  onChange,
  label,
  keyGetter = (item) => item?.id,
  titleGetter = (item) => item?.name || '',
  renderOption: customRenderOption,
  emptyOption,
  disabled = false,
  error = false,
  helperText,
  required = false,
  placeholder,
  startAdornment,
  groupBy,
  filterOptions,
  noOptionsText,
  loading = false,
  loadingText,
  size = 'small',
  fullWidth = true,
  className = 'premium-input',
  sx,
  ...rest
}, ref) => {
  const tokens = usePremiumTokens();

  // Safe token access with fallbacks
  const colors = useMemo(() => ({
    surface: {
      elevated: tokens?.surface?.elevated || '#161b22',
      glass: tokens?.surface?.glass || 'rgba(255, 255, 255, 0.03)',
      hover: tokens?.surface?.hover || 'rgba(255, 255, 255, 0.05)',
    },
    radius: {
      md: tokens?.radius?.md || '8px',
      lg: tokens?.radius?.lg || '12px',
    },
    border: {
      default: tokens?.border?.default || 'rgba(255, 255, 255, 0.08)',
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
      hover: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      secondary: tokens?.text?.secondary || '#8b949e',
      muted: tokens?.text?.muted || '#6e7681',
    },
    semantic: {
      error: tokens?.semantic?.error || '#f85149',
    },
  }), [tokens]);

  // Prepara opcoes com emptyOption se fornecido
  const allOptions = useMemo(() => {
    if (emptyOption) {
      const emptyItem = {
        [typeof keyGetter === 'function' ? 'id' : keyGetter]: emptyOption.value,
        [typeof titleGetter === 'function' ? 'name' : titleGetter]: emptyOption.label,
        _isEmpty: true,
      };
      return [emptyItem, ...options];
    }
    return options;
  }, [options, emptyOption, keyGetter, titleGetter]);

  // Encontra o item selecionado
  const selectedOption = useMemo(() => {
    if (value === null || value === undefined || value === '') return null;
    return allOptions.find((opt) => keyGetter(opt) === value) || null;
  }, [allOptions, value, keyGetter]);

  // Handler de mudanca
  const handleChange = (event, newValue) => {
    const newKey = newValue ? keyGetter(newValue) : (emptyOption?.value ?? null);
    onChange(newKey);
  };

  // Render padrao de opcao
  const defaultRenderOption = (props, option) => {
    const { key, ...restProps } = props;
    const optionKey = keyGetter(option);
    const optionTitle = titleGetter(option);
    const isEmpty = option._isEmpty;

    return (
      <Box
        component="li"
        key={optionKey}
        {...restProps}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 1,
          px: 2,
          color: isEmpty ? colors.text.muted : colors.text.primary,
          fontStyle: isEmpty ? 'italic' : 'normal',
          '&:hover': {
            backgroundColor: `${colors.accent.subtle} !important`,
          },
          '&[aria-selected="true"]': {
            backgroundColor: `${colors.accent.hover} !important`,
          },
        }}
      >
        {startAdornment && !isEmpty && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: colors.text.muted }}>
            {startAdornment}
          </Box>
        )}
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {optionTitle}
        </Typography>
      </Box>
    );
  };

  // Estilos do dropdown
  const dropdownStyles = {
    paper: {
      sx: {
        backgroundColor: 'rgba(22, 27, 34, 0.95)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        borderRadius: colors.radius.lg,
        border: `1px solid ${colors.border.subtle}`,
        mt: 0.5,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        '& .MuiAutocomplete-listbox': {
          maxHeight: 300,
          py: 0.5,
          '& .MuiAutocomplete-option': {
            minHeight: 40,
          },
        },
        '& .MuiAutocomplete-noOptions': {
          color: colors.text.muted,
          padding: '14px 16px',
          fontSize: '0.875rem',
        },
        '& .MuiAutocomplete-loading': {
          color: colors.text.muted,
          padding: '14px 16px',
          fontSize: '0.875rem',
        },
      },
    },
  };

  // Custom filter para pesquisa
  const defaultFilterOptions = (options, { inputValue }) => {
    const searchTerm = inputValue.toLowerCase().trim();
    if (!searchTerm) return options;

    return options.filter((option) => {
      const title = titleGetter(option)?.toLowerCase() || '';
      // Busca por partes da string (permite pesquisar "abc" e encontrar "ABC Device 123")
      return title.includes(searchTerm);
    });
  };

  return (
    <Autocomplete
      ref={ref}
      options={allOptions}
      value={selectedOption}
      onChange={handleChange}
      getOptionLabel={(option) => (option ? titleGetter(option) : '')}
      isOptionEqualToValue={(option, val) => keyGetter(option) === keyGetter(val)}
      disabled={disabled}
      loading={loading}
      loadingText={loadingText || 'Carregando...'}
      noOptionsText={noOptionsText || 'Nenhum resultado encontrado'}
      filterOptions={filterOptions || defaultFilterOptions}
      groupBy={groupBy}
      size={size}
      fullWidth={fullWidth}
      componentsProps={dropdownStyles}
      renderOption={customRenderOption || defaultRenderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          placeholder={placeholder || `Pesquisar ${label?.toLowerCase() || ''}...`}
          error={error}
          helperText={helperText}
          className={className}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.muted, fontSize: 18 }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        '& .MuiOutlinedInput-root': {
          '& .MuiAutocomplete-input': {
            pl: 0,
          },
        },
        ...sx,
      }}
      {...rest}
    />
  );
});

SearchableSelect.displayName = 'SearchableSelect';

/**
 * SearchableSelectMultiple
 * Versao com selecao multipla
 */
export const SearchableSelectMultiple = forwardRef(({
  options = [],
  value = [],
  onChange,
  label,
  keyGetter = (item) => item?.id,
  titleGetter = (item) => item?.name || '',
  disabled = false,
  error = false,
  helperText,
  required = false,
  placeholder,
  noOptionsText,
  loading = false,
  size = 'small',
  fullWidth = true,
  className = 'premium-input',
  limitTags = 3,
  sx,
  ...rest
}, ref) => {
  const tokens = usePremiumTokens();

  const colors = useMemo(() => ({
    accent: {
      primary: tokens?.accent?.primary || '#00d4aa',
      subtle: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
    },
    text: {
      primary: tokens?.text?.primary || '#f0f6fc',
      muted: tokens?.text?.muted || '#6e7681',
    },
    border: {
      subtle: tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)',
    },
    radius: {
      lg: tokens?.radius?.lg || '12px',
    },
  }), [tokens]);

  // Encontra os itens selecionados
  const selectedOptions = useMemo(() => {
    if (!Array.isArray(value)) return [];
    return options.filter((opt) => value.includes(keyGetter(opt)));
  }, [options, value, keyGetter]);

  // Handler de mudanca
  const handleChange = (event, newValue) => {
    const newKeys = newValue.map((item) => keyGetter(item));
    onChange(newKeys);
  };

  const dropdownStyles = {
    paper: {
      sx: {
        backgroundColor: 'rgba(22, 27, 34, 0.95)',
        backdropFilter: 'blur(16px)',
        borderRadius: colors.radius.lg,
        border: `1px solid ${colors.border.subtle}`,
        mt: 0.5,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
      },
    },
  };

  return (
    <Autocomplete
      ref={ref}
      multiple
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      getOptionLabel={(option) => titleGetter(option)}
      isOptionEqualToValue={(option, val) => keyGetter(option) === keyGetter(val)}
      disabled={disabled}
      loading={loading}
      noOptionsText={noOptionsText || 'Nenhum resultado'}
      size={size}
      fullWidth={fullWidth}
      limitTags={limitTags}
      componentsProps={dropdownStyles}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        return (
          <Box component="li" key={keyGetter(option)} {...rest}>
            {titleGetter(option)}
          </Box>
        );
      }}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={keyGetter(option)}
            label={titleGetter(option)}
            size="small"
            sx={{
              backgroundColor: colors.accent.subtle,
              color: colors.accent.primary,
              borderColor: colors.accent.primary,
              '& .MuiChip-deleteIcon': {
                color: colors.accent.primary,
                '&:hover': {
                  color: colors.text.primary,
                },
              },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          placeholder={selectedOptions.length === 0 ? (placeholder || `Pesquisar...`) : ''}
          error={error}
          helperText={helperText}
          className={className}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.text.muted, fontSize: 18 }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
          }}
        />
      )}
      sx={sx}
      {...rest}
    />
  );
});

SearchableSelectMultiple.displayName = 'SearchableSelectMultiple';

export default SearchableSelect;
